import type { EChartsCoreOption } from 'echarts/core';
import type { SeriesResult } from '../data/types';
import { formatCompactCents, formatCents, formatMonth } from '../presentation/format';
import { dimensionColor } from './dimension-colors';

const FALLBACK_COLORS = [
  '#5367d9',
  '#64b6d2',
  '#a08be8',
  '#e986a0',
  '#68bfa1',
  '#f2a179',
  '#d4af4e',
];

export type ChartMode = 'line' | 'bar' | 'area';

export interface CompositionRow {
  name: string;
  value: number;
  colorKey?: string;
}

export interface ChartTheme {
  colors: string[];
  axis: string;
  grid: string;
  text: string;
  surface: string;
  surfaceRaised: string;
  surfaceSubtle: string;
  border: string;
  accent: string;
  accentContrast: string;
  tooltipShadow: string;
  fontSans: string;
}

const FALLBACK_FONT = "'Adwaita Sans', 'Segoe UI Variable Text', 'Segoe UI', system-ui, sans-serif";

export function chartTheme(): ChartTheme {
  if (typeof document === 'undefined') {
    return {
      colors: FALLBACK_COLORS,
      axis: '#444d63',
      grid: 'rgb(83 103 217 / 10%)',
      text: '#242a3d',
      surface: '#ffffff',
      surfaceRaised: '#ffffff',
      surfaceSubtle: '#eef0fa',
      border: '#dde1f0',
      accent: FALLBACK_COLORS[0]!,
      accentContrast: '#ffffff',
      tooltipShadow: 'rgb(38 46 72 / 14%)',
      fontSans: FALLBACK_FONT,
    };
  }
  const style = getComputedStyle(document.documentElement);
  const token = (name: string, fallback: string) => style.getPropertyValue(name).trim() || fallback;
  const accent = token('--accent', FALLBACK_COLORS[0]!);
  return {
    colors: [
      accent,
      ...FALLBACK_COLORS.slice(1).map((fallback, index) => token(`--chart-${index + 2}`, fallback)),
    ],
    axis: token('--chart-axis', '#444d63'),
    grid: token('--chart-grid', 'rgb(83 103 217 / 10%)'),
    text: token('--text', '#242a3d'),
    surface: token('--surface', '#ffffff'),
    surfaceRaised: token('--surface-raised', '#ffffff'),
    surfaceSubtle: token('--surface-subtle', '#eef0fa'),
    border: token('--border', '#dde1f0'),
    accent,
    accentContrast: token('--accent-contrast', '#ffffff'),
    tooltipShadow: token('--chart-tooltip-shadow', 'rgb(38 46 72 / 14%)'),
    fontSans: token('--font-sans', FALLBACK_FONT),
  };
}

function withAlpha(color: string, opacity: number): string {
  const match = /^#([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(color);
  if (!match) return color;
  return `rgba(${Number.parseInt(match[1]!, 16)}, ${Number.parseInt(match[2]!, 16)}, ${Number.parseInt(match[3]!, 16)}, ${opacity})`;
}

function areaGradient(color: string): Record<string, unknown> {
  return {
    type: 'linear',
    x: 0,
    y: 0,
    x2: 0,
    y2: 1,
    colorStops: [
      { offset: 0, color: withAlpha(color, 0.32) },
      { offset: 0.7, color: withAlpha(color, 0.11) },
      { offset: 1, color: withAlpha(color, 0.015) },
    ],
  };
}

function tooltipValue(value: unknown, unit: SeriesResult['unit']): string {
  const scalar = Array.isArray(value) ? value.at(-1) : value;
  const number = typeof scalar === 'number' ? scalar : Number(scalar);
  return unit === 'count' ? new Intl.NumberFormat('it-IT').format(number) : formatCents(number);
}

function escapeHtml(value: unknown): string {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

interface TooltipParameter {
  name?: string;
  seriesName?: string;
  value?: unknown;
  color?: unknown;
}

interface TooltipPositionSize {
  viewSize: [number, number];
}

function tooltipColor(value: unknown): string {
  return typeof value === 'string' && /^#[\da-f]{6}$/i.test(value) ? value : '#687086';
}

function tooltipFormatter(unit: SeriesResult['unit']) {
  return (rawParameters: TooltipParameter | TooltipParameter[]) => {
    const parameters = (Array.isArray(rawParameters) ? rawParameters : [rawParameters]).sort(
      (left, right) => Math.abs(Number(right.value) || 0) - Math.abs(Number(left.value) || 0),
    );
    const nonZeroParameters = parameters.filter((parameter) => {
      const scalar = Array.isArray(parameter.value) ? parameter.value.at(-1) : parameter.value;
      return Number(scalar) !== 0;
    });
    const visibleParameters = nonZeroParameters.length ? nonZeroParameters : parameters.slice(0, 1);
    const hiddenZeroCount = parameters.length - visibleParameters.length;
    const first = parameters[0];
    const rawHeading = first?.name ?? '';
    const heading = rawHeading.length === 7 ? formatMonth(rawHeading) : rawHeading;
    const rows = visibleParameters
      .map((parameter) => {
        const label = parameter.seriesName || parameter.name || 'Valore';
        return `<div class="money-chart-tooltip__row" data-tooltip-series="${escapeHtml(label)}" style="--tooltip-series-color:${tooltipColor(parameter.color)}">
          <span class="money-chart-tooltip__swatch"></span>
          <span class="money-chart-tooltip__label">
            <span class="money-chart-tooltip__label-text">${escapeHtml(label)}</span>
            <span class="money-chart-tooltip__active-state">In analisi</span>
          </span>
          <strong>${escapeHtml(tooltipValue(parameter.value, unit))}</strong>
        </div>`;
      })
      .join('');
    return `<div class="money-chart-tooltip__card">
      ${heading ? `<div class="money-chart-tooltip__heading">${escapeHtml(heading)}</div>` : ''}
      <div class="money-chart-tooltip__list">${rows}</div>
      ${hiddenZeroCount ? `<div class="money-chart-tooltip__summary">${hiddenZeroCount} ${hiddenZeroCount === 1 ? 'serie a zero nascosta' : 'serie a zero nascoste'}</div>` : ''}
    </div>`;
  };
}

function chartBase(theme: ChartTheme, useDecals = false): Record<string, unknown> {
  return {
    animationDuration: 280,
    animationDurationUpdate: 180,
    animationEasing: 'cubicOut',
    animationEasingUpdate: 'cubicOut',
    animationThreshold: 800,
    stateAnimation: { duration: 120, easing: 'cubicOut' },
    color: theme.colors,
    textStyle: {
      color: theme.text,
      fontFamily: theme.fontSans,
      fontSize: 12,
    },
    aria: {
      enabled: true,
      decal: { show: useDecals },
      label: { data: { maxCount: 24 }, series: { maxCount: 12 } },
    },
  };
}

function chartTooltip(
  theme: ChartTheme,
  unit: SeriesResult['unit'] = 'cents',
  trigger: 'axis' | 'item' = 'axis',
): Record<string, unknown> {
  return {
    trigger,
    triggerOn: 'mousemove|click',
    renderMode: 'html',
    className: 'money-chart-tooltip',
    confine: true,
    order: 'valueDesc',
    displayTransition: false,
    transitionDuration: 0,
    enterable: false,
    position: (
      point: [number, number],
      _parameters: unknown,
      _element: HTMLElement,
      _rect: unknown,
      size: TooltipPositionSize,
    ) => (point[0] > size.viewSize[0] / 2 ? { left: 10, top: 10 } : { right: 10, top: 10 }),
    backgroundColor: theme.surfaceRaised,
    borderColor: 'transparent',
    borderWidth: 0,
    borderRadius: 13,
    shadowBlur: 18,
    shadowColor: theme.tooltipShadow,
    shadowOffsetY: 6,
    padding: [10, 12],
    extraCssText:
      'width:min(20rem, calc(100% - 1rem));max-width:calc(100vw - 2rem);box-sizing:border-box;',
    textStyle: {
      color: theme.text,
      fontFamily: theme.fontSans,
      fontSize: 12,
      lineHeight: 19,
    },
    valueFormatter: (value: unknown) => tooltipValue(value, unit),
    formatter: tooltipFormatter(unit),
  };
}

function responsiveMedia(hasEndLabel: boolean): NonNullable<EChartsCoreOption['media']> {
  return [
    {
      query: { maxWidth: 520 },
      option: {
        grid: {
          left: 2,
          right: hasEndLabel ? 62 : 4,
          top: 20,
          bottom: 4,
          containLabel: true,
        },
        xAxis: { axisLabel: { fontSize: 11, fontWeight: 650, margin: 9 } },
        yAxis: { axisLabel: { fontSize: 11, fontWeight: 700, margin: 7 } },
      },
    },
  ];
}

function adaptiveTimeBarWidth(bucketCount: number, seriesCount: number, stacked: boolean): number {
  const groupWidth = bucketCount <= 3 ? 96 : bucketCount <= 6 ? 64 : bucketCount <= 10 ? 40 : 24;
  if (stacked || seriesCount <= 1) return groupWidth;
  return Math.max(10, Math.floor(groupWidth / Math.min(seriesCount, 6)));
}

function adaptiveBarCategoryGap(bucketCount: number): string {
  return bucketCount <= 3 ? '28%' : bucketCount <= 6 ? '34%' : '42%';
}

function adaptiveHorizontalBarWidth(rowCount: number): number {
  return rowCount <= 3 ? 44 : rowCount <= 6 ? 32 : 22;
}

function mapComponent(
  value: unknown,
  transform: (component: Record<string, unknown>) => Record<string, unknown>,
): Record<string, unknown> | Record<string, unknown>[] | undefined {
  if (Array.isArray(value))
    return value.map((component) => transform((component ?? {}) as Record<string, unknown>));
  if (!value || typeof value !== 'object') return undefined;
  return transform(value as Record<string, unknown>);
}

export function chartThemePatch(option: EChartsCoreOption): EChartsCoreOption {
  const theme = chartTheme();
  const source = option as Record<string, unknown>;
  const legend = mapComponent(source.legend, () => ({
    textStyle: { color: theme.axis, fontFamily: theme.fontSans },
    selectorLabel: {
      color: theme.axis,
      backgroundColor: theme.surfaceSubtle,
      fontFamily: theme.fontSans,
      borderRadius: 99,
    },
    pageTextStyle: { color: theme.axis, fontFamily: theme.fontSans },
    pageIconColor: theme.accent,
    pageIconInactiveColor: theme.border,
  }));
  const axis = (value: unknown) =>
    mapComponent(value, () => ({
      axisLabel: { color: theme.axis, fontFamily: theme.fontSans },
      axisLine: { lineStyle: { color: theme.grid } },
      splitLine: { lineStyle: { color: theme.grid } },
      axisPointer: {
        lineStyle: { color: theme.accent },
        label: {
          color: theme.accentContrast,
          backgroundColor: theme.accent,
          fontFamily: theme.fontSans,
        },
      },
    }));
  const series = Array.isArray(source.series)
    ? source.series.map((item) => {
        const current = (item ?? {}) as Record<string, unknown>;
        const type = current.type;
        return {
          id: current.id,
          ...(type === 'pie'
            ? {
                itemStyle: { borderColor: theme.surface },
                label: { color: theme.text, fontFamily: theme.fontSans },
                emphasis: { label: { color: theme.text, fontFamily: theme.fontSans } },
                select: { label: { color: theme.text, fontFamily: theme.fontSans } },
              }
            : {}),
          ...(type === 'bar' && current.showBackground
            ? { backgroundStyle: { color: theme.grid } }
            : {}),
          ...(current.endLabel
            ? {
                endLabel: {
                  color: theme.text,
                  backgroundColor: theme.surfaceRaised,
                  borderColor: theme.border,
                  fontFamily: theme.fontSans,
                },
              }
            : {}),
        };
      })
    : undefined;

  return {
    color: source.color ?? theme.colors,
    textStyle: { color: theme.text, fontFamily: theme.fontSans },
    darkMode: document.documentElement.dataset.theme === 'dark',
    legend,
    tooltip: source.tooltip
      ? {
          backgroundColor: theme.surfaceRaised,
          borderColor: 'transparent',
          shadowBlur: 18,
          shadowColor: theme.tooltipShadow,
          textStyle: { color: theme.text, fontFamily: theme.fontSans },
        }
      : undefined,
    xAxis: axis(source.xAxis),
    yAxis: axis(source.yAxis),
    series,
  };
}

export function toTimeChartOption(
  result: SeriesResult,
  mode: ChartMode = 'line',
): EChartsCoreOption {
  const theme = chartTheme();
  const hasLegend = result.series.length > 1;
  const hasEndLabel = result.series.length === 1 && mode !== 'bar';
  const allValues = result.series.flatMap((series) => series.values);
  const stackBars =
    mode === 'bar' &&
    result.series.length > 1 &&
    (allValues.every((value) => value >= 0) || allValues.every((value) => value <= 0));
  const seriesColors = result.series.map((series) =>
    dimensionColor(series.colorKey ?? `series:${series.id}`, series.label),
  );
  const barMaxWidth = adaptiveTimeBarWidth(result.buckets.length, result.series.length, stackBars);
  const barCategoryGap = adaptiveBarCategoryGap(result.buckets.length);
  return {
    ...chartBase(theme),
    color: seriesColors,
    media: responsiveMedia(hasEndLabel),
    grid: {
      left: 12,
      right: hasEndLabel ? 88 : 12,
      top: 24,
      bottom: 10,
      containLabel: true,
    },
    legend: hasLegend
      ? {
          show: false,
          type: 'plain',
          data: result.series.map((series) => series.label),
        }
      : undefined,
    tooltip: {
      ...chartTooltip(theme, result.unit),
      trigger: 'axis',
      axisPointer: {
        type: mode === 'bar' ? 'shadow' : 'line',
        axis: 'x',
        snap: true,
        animation: true,
        animationDurationUpdate: 80,
        lineStyle: { color: theme.accent, width: 2, type: 'solid', opacity: 0.55 },
        shadowStyle: { color: theme.grid },
        label: {
          show: true,
          color: theme.accentContrast,
          backgroundColor: theme.accent,
          borderRadius: 8,
          padding: [5, 7],
          fontFamily: theme.fontSans,
          fontSize: 12,
          fontWeight: 750,
          formatter: ({ value }: { value: unknown }) => {
            const label = String(value);
            return label.length === 7 ? formatMonth(label) : label;
          },
        },
      },
    },
    xAxis: {
      type: 'category',
      data: result.buckets,
      boundaryGap: mode === 'bar',
      triggerEvent: true,
      axisLabel: {
        color: theme.axis,
        fontFamily: theme.fontSans,
        fontSize: 12,
        fontWeight: 650,
        hideOverlap: true,
        margin: 11,
        formatter: (value: string) => (value.length === 7 ? formatMonth(value) : value),
      },
      axisLine: { show: false },
      axisTick: { show: false },
      axisPointer: { snap: true },
    },
    yAxis: {
      type: 'value',
      splitNumber: 4,
      axisLabel: {
        color: theme.axis,
        fontFamily: theme.fontSans,
        fontSize: 13,
        fontWeight: 700,
        margin: 9,
        formatter: (value: number) =>
          result.unit === 'count' ? String(value) : formatCompactCents(value),
      },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: theme.grid, type: 'solid' } },
    },
    series: result.series.map((series, seriesIndex) =>
      mode === 'bar'
        ? {
            id: series.id,
            name: series.label,
            type: 'bar',
            itemStyle: { color: seriesColors[seriesIndex] },
            data: series.values.map((value) => ({
              value,
              itemStyle: {
                color: seriesColors[seriesIndex],
                borderRadius: stackBars ? 4 : value < 0 ? [2, 2, 7, 7] : [7, 7, 2, 2],
              },
            })),
            stack: stackBars ? 'total' : undefined,
            stackStrategy: stackBars ? 'samesign' : undefined,
            barMaxWidth,
            barMinHeight: 2,
            barGap: '20%',
            barCategoryGap,
            clip: true,
            emphasis: {
              focus: 'series',
              itemStyle: {
                borderColor: theme.text,
                borderWidth: 2,
                shadowBlur: 12,
                shadowColor: theme.tooltipShadow,
              },
            },
            blur: { itemStyle: { opacity: 0.16 } },
          }
        : {
            id: series.id,
            name: series.label,
            type: 'line',
            itemStyle: { color: seriesColors[seriesIndex] },
            data: series.values,
            sampling: result.buckets.length > 160 ? 'lttb' : 'none',
            smooth: 0.42,
            smoothMonotone: 'x',
            connectNulls: false,
            showSymbol: false,
            symbol: 'circle',
            symbolSize: 8,
            triggerEvent: mode === 'area' ? 'area' : true,
            clip: true,
            endLabel: hasEndLabel
              ? {
                  show: true,
                  distance: 7,
                  valueAnimation: true,
                  color: theme.text,
                  backgroundColor: theme.surfaceRaised,
                  borderColor: 'transparent',
                  borderWidth: 0,
                  borderRadius: 10,
                  padding: [5, 8],
                  shadowBlur: 12,
                  shadowColor: theme.tooltipShadow,
                  fontFamily: theme.fontSans,
                  fontSize: 12,
                  fontWeight: 700,
                  formatter: ({ value }: { value: unknown }) =>
                    result.unit === 'count'
                      ? new Intl.NumberFormat('it-IT').format(Number(value))
                      : formatCompactCents(Number(value)),
                }
              : undefined,
            areaStyle:
              mode === 'area'
                ? {
                    opacity: 1,
                    origin: 'auto',
                    color: areaGradient(seriesColors[seriesIndex]!),
                  }
                : undefined,
            lineStyle: {
              color: seriesColors[seriesIndex],
              width: 3,
              cap: 'round',
              join: 'round',
            },
            emphasis: {
              focus: 'series',
              scale: 1.15,
              lineStyle: { width: 'bolder' },
            },
            blur: {
              lineStyle: { opacity: 0.16 },
              areaStyle: { opacity: 0.025 },
            },
          },
    ),
  };
}

export function toCompositionOption(
  rows: ReadonlyArray<CompositionRow>,
  mode: 'bar' | 'donut' = 'bar',
  options: { tooltip?: boolean } = {},
): EChartsCoreOption {
  const theme = chartTheme();
  const tooltipEnabled = options.tooltip !== false;
  const rowColors = rows.map((row, index) =>
    dimensionColor(row.colorKey ?? `composition:${row.name}:${index}`, row.name),
  );
  if (mode === 'donut') {
    const total = rows.reduce((sum, row) => sum + row.value, 0);
    return {
      ...chartBase(theme),
      color: rowColors,
      tooltip: tooltipEnabled
        ? chartTooltip(theme, 'cents', 'item')
        : { show: false, trigger: 'none' },
      legend: {
        show: false,
        type: 'plain',
        data: rows.map((row) => row.name),
      },
      media: [
        {
          query: { maxWidth: 420 },
          option: {
            series: [{ radius: ['42%', '66%'], center: ['50%', '50%'] }],
          },
        },
      ],
      series: [
        {
          type: 'pie',
          radius: ['46%', '70%'],
          center: ['50%', '50%'],
          startAngle: 90,
          padAngle: 2.5,
          minAngle: 2,
          minShowLabelAngle: 4,
          avoidLabelOverlap: true,
          stillShowZeroSum: false,
          percentPrecision: 1,
          selectedMode: 'single',
          selectedOffset: 4,
          animationType: 'scale',
          animationTypeUpdate: 'transition',
          itemStyle: {
            borderColor: theme.surface,
            borderWidth: 4,
            borderRadius: 8,
          },
          label: {
            show: false,
            position: 'outside',
            color: theme.text,
            fontFamily: theme.fontSans,
          },
          labelLine: { show: false },
          emphasis: {
            focus: 'self',
            scale: true,
            scaleSize: 5,
            label: {
              show: true,
              position: 'outside',
              color: theme.text,
              fontFamily: theme.fontSans,
              fontSize: 14,
              fontWeight: 700,
              lineHeight: 21,
              formatter: ({ name, percent }: { name: string; percent: number }) =>
                `${name}\n${new Intl.NumberFormat('it-IT', { maximumFractionDigits: 1 }).format(percent)}%`,
            },
            labelLine: { show: true, length: 10, length2: 6, smooth: 0.2 },
          },
          select: {
            label: {
              show: true,
              position: 'outside',
              color: theme.text,
              fontFamily: theme.fontSans,
              fontSize: 14,
              fontWeight: 700,
              lineHeight: 21,
              formatter: ({ name, percent }: { name: string; percent: number }) =>
                `${name}\n${new Intl.NumberFormat('it-IT', { maximumFractionDigits: 1 }).format(percent)}%`,
            },
            labelLine: { show: true, length: 10, length2: 6, smooth: 0.2 },
          },
          blur: { itemStyle: { opacity: 0.24 } },
          data: rows.map((row, index) => ({
            ...row,
            itemStyle: { color: rowColors[index] },
            label:
              index === 0
                ? {
                    show: true,
                    position: 'center',
                    formatter: `{value|${formatCompactCents(total)}}\n{caption|Totale}`,
                    rich: {
                      value: {
                        color: theme.text,
                        fontFamily: theme.fontSans,
                        fontSize: 17,
                        fontWeight: 700,
                        lineHeight: 24,
                      },
                      caption: {
                        color: theme.axis,
                        fontFamily: theme.fontSans,
                        fontSize: 12,
                        fontWeight: 650,
                        lineHeight: 17,
                      },
                    },
                  }
                : undefined,
          })),
        },
      ],
    };
  }

  return {
    ...chartBase(theme),
    color: rowColors,
    grid: { left: 8, right: 86, top: 8, bottom: 8, containLabel: true },
    tooltip: tooltipEnabled
      ? {
          ...chartTooltip(theme, 'cents'),
          axisPointer: { type: 'shadow', shadowStyle: { color: theme.grid } },
        }
      : { show: false, trigger: 'none' },
    xAxis: {
      type: 'value',
      splitNumber: 3,
      axisLabel: {
        show: false,
      },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: theme.grid, type: 'solid' } },
    },
    yAxis: {
      type: 'category',
      data: rows.map((row) => row.name),
      inverse: true,
      axisTick: { show: false },
      axisLine: { show: false },
      axisLabel: {
        interval: 0,
        width: 200,
        overflow: 'truncate',
        ellipsis: '…',
        color: theme.text,
        fontFamily: theme.fontSans,
        fontSize: 13,
        fontWeight: 650,
        lineHeight: 18,
        margin: 12,
        tooltip: { show: tooltipEnabled },
      },
    },
    series: [
      {
        type: 'bar',
        colorBy: 'data',
        data: rows.map((row, index) => ({
          name: row.name,
          value: row.value,
          itemStyle: {
            color: rowColors[index],
            borderRadius: row.value < 0 ? [9, 2, 2, 9] : [2, 9, 9, 2],
          },
          label: { position: 'right' },
        })),
        label: {
          show: true,
          distance: 8,
          color: theme.text,
          fontFamily: theme.fontSans,
          fontSize: 13,
          fontWeight: 700,
          formatter: ({ value }: { value: unknown }) => formatCompactCents(Number(value)),
        },
        barMaxWidth: adaptiveHorizontalBarWidth(rows.length),
        barMinHeight: 2,
        showBackground: true,
        backgroundStyle: { color: theme.grid, borderRadius: 9 },
        emphasis: { focus: 'series' },
      },
    ],
  };
}
